import { useEffect, useState, type KeyboardEvent } from 'react';

import { Modal } from '@/components/Modal';
import {
  completePasswordReset,
  confirmPasswordReset,
  startPasswordReset,
  type PasswordResetChallenge,
} from '@/lib/auth/session';

type Step = 'identify' | 'code' | 'password';

function useSecondsLeft(deadline: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (deadline === null) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [deadline]);
  return deadline === null ? 0 : Math.max(0, Math.ceil((deadline - now) / 1000));
}

function formatSeconds(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

function messageOf(cause: unknown): string {
  return cause instanceof Error ? cause.message : '요청을 처리하지 못했습니다.';
}

/**
 * 비밀번호 찾기 — 휴대폰 인증 세 걸음(V407).
 *
 * 창까지 이 파일이 그린다 — 다음 걸음 버튼이 제목 줄의 「닫기」 옆에 서야 하는데, 눌릴 수 있는지를 아는
 * 것이 이 폼이다(RegisterForm 과 같은 판단).
 */
export function PasswordResetForm({
  initialEmail,
  onClose,
  onDone,
}: {
  initialEmail: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [step, setStep] = useState<Step>('identify');
  const [email, setEmail] = useState(initialEmail);
  const [name, setName] = useState('');
  const [challenge, setChallenge] = useState<PasswordResetChallenge | null>(null);
  const [deadline, setDeadline] = useState<number | null>(null);
  const [code, setCode] = useState('');
  const [ticket, setTicket] = useState('');
  const [nextPassword, setNextPassword] = useState('');
  const [againPassword, setAgainPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const secondsLeft = useSecondsLeft(deadline);

  const mismatch = againPassword !== '' && nextPassword !== againPassword;
  const canSend = !pending && email.trim() !== '' && name.trim() !== '';
  const canConfirm = !pending && challenge !== null && code !== '' && secondsLeft > 0;
  const canFinish = !pending && ticket !== '' && nextPassword !== '' && nextPassword === againPassword;

  const run = async (action: () => Promise<void>) => {
    setPending(true);
    setError(null);
    try {
      await action();
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setPending(false);
    }
  };

  const send = () => {
    if (!canSend) return;
    void run(async () => {
      const sent = await startPasswordReset({ email: email.trim(), name: name.trim() });
      setChallenge(sent);
      setDeadline(Date.now() + sent.expiresInSeconds * 1000);
      setCode('');
      setStep('code');
    });
  };

  const verify = () => {
    if (!canConfirm || !challenge) return;
    void run(async () => {
      setTicket(await confirmPasswordReset({ verificationId: challenge.verificationId, code }));
      setStep('password');
    });
  };

  const finish = () => {
    if (!canFinish) return;
    void run(async () => {
      await completePasswordReset({ ticket, newPassword: nextPassword });
      onDone();
    });
  };

  const onEnter = (action: () => void) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) action();
  };

  return (
    <Modal
      title="비밀번호 찾기"
      onClose={() => !pending && onClose()}
      maxWidth="28rem"
      actions={
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={pending}>
            닫기
          </button>
          {step === 'identify' && (
            <button type="button" className="btn-primary" onClick={send} disabled={!canSend}>
              {pending ? '보내는 중…' : '인증번호 받기'}
            </button>
          )}
          {step === 'code' && (
            <>
              <button type="button" className="btn-secondary" onClick={send} disabled={!canSend}>
                다시 받기
              </button>
              <button type="button" className="btn-primary" onClick={verify} disabled={!canConfirm}>
                {pending ? '확인 중…' : '확인'}
              </button>
            </>
          )}
          {step === 'password' && (
            <button type="button" className="btn-primary" onClick={finish} disabled={!canFinish}>
              {pending ? '변경 중…' : '변경'}
            </button>
          )}
        </>
      }
    >
      {step === 'identify' && (
        <>
          <label className="mb-3 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>
              아이디
            </span>
            <input
              type="email"
              className="input-field"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              onKeyDown={onEnter(send)}
              autoComplete="username"
              inputMode="email"
              placeholder="이메일"
            />
          </label>
          <label className="mb-3 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>
              이름
            </span>
            <input
              className="input-field"
              value={name}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={onEnter(send)}
            />
          </label>
        </>
      )}

      {step === 'code' && challenge && (
        <label className="mb-3 block">
          <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>
            인증번호 · {challenge.maskedPhone} · {formatSeconds(secondsLeft)}
          </span>
          <input
            className="input-field"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            onKeyDown={onEnter(verify)}
            autoFocus
          />
        </label>
      )}

      {step === 'password' && (
        <>
          <label className="mb-3 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>
              새 비밀번호
            </span>
            <input
              type="password"
              className="input-field"
              value={nextPassword}
              onChange={(event) => setNextPassword(event.target.value)}
              onKeyDown={onEnter(finish)}
              autoComplete="new-password"
              autoFocus
            />
          </label>
          <label className="mb-3 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>
              새 비밀번호 확인
            </span>
            <input
              type="password"
              className="input-field"
              value={againPassword}
              onChange={(event) => setAgainPassword(event.target.value)}
              onKeyDown={onEnter(finish)}
              autoComplete="new-password"
            />
          </label>
          {mismatch && (
            <p className="mb-3 text-ui-xs" style={{ color: 'var(--danger)' }}>
              두 비밀번호가 다릅니다.
            </p>
          )}
        </>
      )}

      {error && (
        <p className="text-ui-xs" style={{ color: 'var(--danger)' }}>
          {error}
        </p>
      )}
    </Modal>
  );
}
